import { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";
import { TrendingUp, TrendingDown, IndianRupee, Briefcase, CheckCircle2, Clock, ListTodo, AlertTriangle, Users2 } from "lucide-react";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

function Kpi({ icon: Icon, label, value, sub, tone = "default", testid }) {
  const tones = {
    default: "text-foreground",
    good: "text-accent",
    warn: "text-primary",
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-6 bg-card border border-border/60 hover:border-primary/40 transition-colors"
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{label}</div>
        <div className="w-8 h-8 rounded-lg bg-muted/60 flex items-center justify-center">
          <Icon className="w-4 h-4 text-primary" strokeWidth={1.6} />
        </div>
      </div>
      <div className={`mt-4 font-display text-3xl font-semibold tracking-tight ${tones[tone]}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </motion.div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Studio Overview</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">Good morning.</h1>
          <p className="text-muted-foreground mt-2 text-sm">Here's the pulse of your studio today — {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-revenue" icon={IndianRupee} label="Revenue" value={fmt(stats?.revenue)} sub="Collected from paid invoices" tone="good" />
        <Kpi testid="kpi-expenses" icon={TrendingDown} label="Expenses" value={fmt(stats?.expenses)} sub="All categories" />
        <Kpi testid="kpi-profit" icon={TrendingUp} label="Profit" value={fmt(stats?.profit)} sub="Revenue − expenses" tone="good" />
        <Kpi testid="kpi-pending" icon={Clock} label="Pending Payments" value={fmt(stats?.pending_payments)} sub="Awaiting collection" tone="warn" />
        <Kpi testid="kpi-running" icon={Briefcase} label="Running Projects" value={stats?.running_projects ?? "—"} />
        <Kpi testid="kpi-completed" icon={CheckCircle2} label="Completed" value={stats?.completed_projects ?? "—"} tone="good" />
        <Kpi testid="kpi-tasks" icon={ListTodo} label="Today's Tasks" value={stats?.today_tasks ?? 0} />
        <Kpi testid="kpi-stock" icon={AlertTriangle} label="Low Stock Alerts" value={stats?.low_stock_count ?? 0} tone="warn" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl p-6 bg-card border border-border/60">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Monthly Sales</div>
              <div className="font-display text-xl mt-1">Invoice revenue trend</div>
            </div>
            <TrendingUp className="w-4 h-4 text-primary" />
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={stats?.monthly_series || []}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="amount" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ fill: "hsl(var(--primary))", r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl p-6 bg-card border border-border/60">
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Team</div>
          <div className="font-display text-xl mt-1 mb-4">Studio at a glance</div>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/40" data-testid="dash-active-team">
              <div className="flex items-center gap-2"><Users2 className="w-4 h-4 text-accent" /> Active team</div>
              <span className="font-medium">{stats?.active_team ?? 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/40">
              <div className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-primary" /> Active projects</div>
              <span className="font-medium">{stats?.running_projects ?? 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/40">
              <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-primary" /> Material alerts</div>
              <span className="font-medium">{stats?.low_stock_count ?? 0}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl p-6 bg-card border border-border/60">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Progress</div>
            <div className="font-display text-xl mt-1">Project completion %</div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={stats?.project_progress || []}>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} domain={[0, 100]} />
            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
            <Bar dataKey="progress" fill="hsl(var(--accent))" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
